import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { decodeJwt } from "jose";
import { Auth } from "../store/authorize";
import { authStore } from "./authStore";
import { applyAuth, syncFromStorage } from "./authState";
import { logout } from "./logout";

/**
 * The app's HTTP layer. ONE request + ONE response interceptor are registered here at module load (app lifetime),
 * reading the live token from authStore (never a React closure). Refresh is single-flight: concurrent 401s share one
 * refresh; the original requests replay with the new token. Proactive: a token within the skew window is refreshed
 * before the request goes out, which avoids the 401-storm on app-load with a stale token.
 */

export const http = axios.create({
  baseURL: process.env.REACT_APP_API_HOST,
  timeout: 30000, // 30s — heavy admin loads (questionnaires/KPIs) exceeded the old 10s on UAT
  headers: { "Content-Type": "application/json" },
});

// No interceptors — used for the auth endpoints (login/refresh) so they can never re-enter the refresh flow.
export const publicHttp = axios.create({
  baseURL: process.env.REACT_APP_API_HOST,
  timeout: 30000, // 30s — heavy admin loads (questionnaires/KPIs) exceeded the old 10s on UAT
  headers: { "Content-Type": "application/json" },
});

// ----- network status (replaces the old setNointernet in the interceptor) -----
type NetworkListener = (offline: boolean) => void;
const networkListeners = new Set<NetworkListener>();
export function onNetworkStatus(listener: NetworkListener): () => void {
  networkListeners.add(listener);
  return () => {
    networkListeners.delete(listener);
  };
}
function setOffline(offline: boolean): void {
  networkListeners.forEach((l) => l(offline));
}

// ----- helpers -----
const AUTH_ENDPOINTS = ["/api/Auth/Login", "/api/Auth/RefreshToken"];
const isAuthEndpoint = (url?: string): boolean => !!url && AUTH_ENDPOINTS.some((e) => url.includes(e));
const SKEW_SECONDS = 60;

function isExpired(token: string, withinSeconds = 0): boolean {
  try {
    const { exp } = decodeJwt(token);
    if (!exp) return false;
    return exp * 1000 <= Date.now() + withinSeconds * 1000;
  } catch {
    return true; // unparseable → treat as expired so we refresh/logout rather than send garbage
  }
}

function readAuthError(error: AxiosError): "expired" | "invalid" | undefined {
  const ext = (error.response?.data as { authError?: string } | undefined)?.authError;
  if (ext === "expired" || ext === "invalid") return ext;
  const header = error.response?.headers?.["www-authenticate"];
  if (typeof header === "string") {
    if (/access token is invalid/i.test(header)) return "invalid";
    if (/access token has expired/i.test(header)) return "expired";
  }
  return undefined;
}

// ----- single-flight refresh -----
let refreshPromise: Promise<Auth> | null = null;

async function callRefresh(auth: Auth): Promise<Auth> {
  const { data } = await publicHttp.post(
    "/api/Auth/RefreshToken",
    { token: auth.token, refreshToken: auth.refreshToken },
    { headers: { Authorization: `Bearer ${auth.token}` } },
  );
  return data as Auth;
}

/**
 * Returns a usable access token, refreshing first (via single-flight) if it's missing/expired/near-expiry.
 * Used by the SignalR accessTokenFactory so each (re)connect is auth-aware: a genuine expiry triggers a refresh
 * before the handshake instead of looping on a stale token. Returns "" if there are no credentials.
 */
export async function getFreshAccessToken(): Promise<string> {
  const auth = authStore.get();
  if (auth?.token && auth?.refreshToken && isExpired(auth.token, SKEW_SECONDS)) {
    try {
      await runRefresh();
    } catch {
      // leave it to the caller's retry — a down backend will recover on a later attempt
    }
  }
  return authStore.get()?.token ?? "";
}

function runRefresh(): Promise<Auth> {
  if (refreshPromise) return refreshPromise;

  // Adopt a token another tab may have just refreshed (rotating refresh tokens reject reuse, so a stale in-memory
  // token would 400). If that already gave us a still-valid token, no network refresh is needed.
  syncFromStorage();
  const auth = authStore.get();
  if (!auth?.token || !auth?.refreshToken) {
    return Promise.reject(new Error("no-credentials"));
  }
  if (!isExpired(auth.token, SKEW_SECONDS)) {
    return Promise.resolve(auth);
  }

  refreshPromise = callRefresh(auth)
    .then((next) => {
      applyAuth(next); // refresh: reuse the existing storage choice
      return next;
    })
    .catch((err) => {
      // Multi-tab race: another tab rotated the refresh token while we were trying, so ours 400'd. If storage now
      // holds a fresh, valid token from that tab, adopt it and carry on instead of a spurious logout.
      const latest = syncFromStorage();
      if (latest?.token && latest.refreshToken !== auth.refreshToken && !isExpired(latest.token, SKEW_SECONDS)) {
        return latest;
      }
      throw err;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

// ----- request interceptor: proactive refresh + attach bearer -----
http.interceptors.request.use(async (config) => {
  if (isAuthEndpoint(config.url)) return config;

  const auth = authStore.get();
  if (auth?.token && auth?.refreshToken && isExpired(auth.token, SKEW_SECONDS)) {
    try {
      await runRefresh();
    } catch {
      // let the response 401 path perform a single clean logout
    }
  }

  const token = authStore.get()?.token;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ----- response interceptor: single-flight reactive refresh, correct retry, graceful logout -----
http.interceptors.response.use(
  (response) => {
    setOffline(false);
    return response;
  },
  async (error: AxiosError) => {
    // Guarded: no response means a network/timeout/setup error (the old code dereferenced error.request → crash).
    if (!error.response) {
      setOffline(true);
      return Promise.reject(error);
    }
    setOffline(false);

    const status = error.response.status;
    const config = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;

    // 403 = authenticated but not allowed → surface it; never refresh or logout.
    if (status === 403) return Promise.reject(error);

    if (status === 401 && config && !isAuthEndpoint(config.url)) {
      const serverInvalid = readAuthError(error) === "invalid";

      // A structurally-invalid token, or a second failure after we already retried, is not refreshable → logout.
      if (config._retry || serverInvalid) {
        await logout("session-expired");
        return Promise.reject(error);
      }

      config._retry = true;
      try {
        const next = await runRefresh();
        config.headers.Authorization = `Bearer ${next.token}`;
        return http(config); // replay; do NOT fall through to reject
      } catch {
        await logout("session-expired");
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  },
);
